import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe,
  approvePost, deletePost, hidePost, unhidePost, clearPostReports,
  fetchPendingCommunityPosts, fetchReportedCommunityPosts, fetchHiddenCommunityPosts,
  fetchAllCommunityPosts,
  fetchCommunityComments, hideComment, unhideComment, deleteComment, clearCommentReports,
  fetchCommunityCategories, createCommunityCategory, updateCommunityCategory, deleteCommunityCategory,
  CommunityCategoryRow,
} from '../lib/admin-portal-api';
import { FaCheckCircle, FaClipboardList, FaExclamationTriangle } from '../lib/icons';

interface Props { me: AdminMe }

type Tab = 'all' | 'pending' | 'reported' | 'hidden' | 'comments' | 'categories';

export default function AdminPortalCommunity({ me }: Props) {
  const [tab, setTab] = useState<Tab>('all');

  return (
    <AdminPortalLayout me={me} active="community">
      <Helmet><title>Community — EatMeat Admin</title></Helmet>

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Community</h1>
          <p className="text-sm text-gray-500 mt-1">Pending, reported, hidden posts · comments · categories.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex gap-1 overflow-x-auto">
          {([
            { id: 'all',        label: 'All' },
            { id: 'pending',    label: 'Pending' },
            { id: 'reported',   label: 'Reported' },
            { id: 'hidden',     label: 'Hidden' },
            { id: 'comments',   label: 'Comments' },
            { id: 'categories', label: 'Categories' },
          ] as { id: Tab; label: string }[]).map(t => (
            <button key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'all' && <AllPostsPanel />}
      {tab === 'pending' && <PostList kind="pending" />}
      {tab === 'reported' && <PostList kind="reported" />}
      {tab === 'hidden' && <PostList kind="hidden" />}
      {tab === 'comments' && <CommentsPanel />}
      {tab === 'categories' && <CategoriesPanel />}
    </AdminPortalLayout>
  );
}

/* ---------- Posts list (pending/reported/hidden) ---------- */
function PostList({ kind }: { kind: 'pending' | 'reported' | 'hidden' }) {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const fn = kind === 'pending' ? fetchPendingCommunityPosts
        : kind === 'reported' ? fetchReportedCommunityPosts
        : fetchHiddenCommunityPosts;
      const res = await fn();
      setPosts(res.data || []);
    } catch {
      setError('Failed to load.');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  const wrap = async (id: number, fn: () => Promise<any>, keepRow = false) => {
    setBusy(id);
    try {
      await fn();
      if (keepRow) await load();
      else setPosts((list) => list.filter((p) => p.id !== id));
    } catch { alert('Action failed'); }
    finally { setBusy(null); }
  };

  if (error) {
    return (
      <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2">
        <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
      </div>
    );
  }
  if (posts.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <FaCheckCircle className="text-4xl mx-auto mb-3 text-green-300" />
        <p className="text-sm text-gray-500">Nothing here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((p) => (
        <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-800">{p.user?.username || 'Anonymous'}</span>
                {p.category?.name && <span className="text-gray-300">·</span>}
                {p.category?.name && <span className="text-xs text-gray-500">{p.category.name}</span>}
                {p.report_count > 0 && (
                  <span className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">🚩 {p.report_count}</span>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">
                {new Date(p.createdAt).toLocaleString()}
                {kind === 'hidden' && p.updatedAt ? ` · hidden ${new Date(p.updatedAt).toLocaleDateString()}` : ''}
              </p>
              <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap break-words">{p.content}</p>
            </div>
            <FaClipboardList className={`text-2xl shrink-0 ${kind === 'reported' ? 'text-red-300' : kind === 'hidden' ? 'text-gray-300' : 'text-amber-200'}`} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            {kind === 'reported' && (
              <button disabled={busy === p.id}
                onClick={() => wrap(p.id, () => clearPostReports(p.id), true)}
                className="text-xs px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 font-semibold disabled:opacity-50">
                Clear reports
              </button>
            )}
            {kind === 'hidden' ? (
              <button disabled={busy === p.id}
                onClick={() => wrap(p.id, () => unhidePost(p.id))}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
                Restore
              </button>
            ) : (
              <button disabled={busy === p.id}
                onClick={() => wrap(p.id, () => hidePost(p.id))}
                className="text-xs px-3 py-1.5 rounded-md bg-yellow-50 text-yellow-800 hover:bg-yellow-100 font-semibold disabled:opacity-50">
                Hide
              </button>
            )}
            <button disabled={busy === p.id}
              onClick={() => {
                if (!window.confirm('Delete this post + all its comments?')) return;
                wrap(p.id, () => deletePost(p.id));
              }}
              className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">
              Delete
            </button>
            {kind !== 'hidden' && (
              <button disabled={busy === p.id}
                onClick={() => wrap(p.id, () => approvePost(p.id))}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
                Approve
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- All posts ---------- */
function AllPostsPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (pageNum = 1, query = q, append = false) => {
    if (!append) setLoading(true);
    try {
      const res = await fetchAllCommunityPosts(query, pageNum, 20);
      setRows((prev) => append ? [...prev, ...(res.data || [])] : (res.data || []));
      setPage(pageNum);
      setPageCount(res.pagination?.pageCount || 1);
      setTotal(res.pagination?.total || 0);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1, ''); }, []);

  const onSearch = (e: React.FormEvent) => { e.preventDefault(); load(1, q.trim()); };

  const wrap = async (id: number, fn: () => Promise<any>, removeRow = false) => {
    setBusy(id);
    try {
      await fn();
      if (removeRow) setRows((list) => list.filter((p) => p.id !== id));
      else await load(page, q);
    } catch { alert('Action failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={onSearch} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search post content…"
          className="flex-1 px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none"
        />
        <button type="submit" disabled={loading}
          className="px-5 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {loading ? 'Searching…' : 'Search'}
        </button>
        {q && (
          <button type="button" onClick={() => { setQ(''); load(1, ''); }}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">Clear</button>
        )}
      </form>

      {loading && rows.length === 0 ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaClipboardList className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No posts.</p>
        </div>
      ) : (
        <>
          {rows.map((p) => {
            const status = p.is_hidden ? { label: 'Hidden', cls: 'bg-gray-100 text-gray-700' }
              : !p.is_approved ? { label: 'Pending', cls: 'bg-amber-50 text-amber-800' }
              : { label: 'Live', cls: 'bg-green-50 text-green-700' };
            return (
              <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2 text-sm">
                      <span className="font-semibold text-gray-800">{p.user?.username || 'Anonymous'}</span>
                      {p.category?.name && <span className="text-gray-300">·</span>}
                      {p.category?.name && <span className="text-xs text-gray-500">{p.category.name}</span>}
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
                      {p.is_pinned && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">📌 Pinned</span>}
                      {p.report_count > 0 && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">🚩 {p.report_count}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {new Date(p.createdAt).toLocaleString()} · ❤ {p.likes_count} · 💬 {p.comments_count}
                    </p>
                    <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap break-words">{p.content}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  {!p.is_approved && (
                    <button disabled={busy === p.id}
                      onClick={() => wrap(p.id, () => approvePost(p.id))}
                      className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
                      Approve
                    </button>
                  )}
                  {p.is_hidden ? (
                    <button disabled={busy === p.id}
                      onClick={() => wrap(p.id, () => unhidePost(p.id))}
                      className="text-xs px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 font-semibold disabled:opacity-50">
                      Unhide
                    </button>
                  ) : (
                    <button disabled={busy === p.id}
                      onClick={() => wrap(p.id, () => hidePost(p.id))}
                      className="text-xs px-3 py-1.5 rounded-md bg-yellow-50 text-yellow-800 hover:bg-yellow-100 font-semibold disabled:opacity-50">
                      Hide
                    </button>
                  )}
                  {p.report_count > 0 && (
                    <button disabled={busy === p.id}
                      onClick={() => wrap(p.id, () => clearPostReports(p.id))}
                      className="text-xs px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 font-semibold disabled:opacity-50">
                      Clear reports
                    </button>
                  )}
                  <button disabled={busy === p.id}
                    onClick={() => { if (!window.confirm('Delete this post + all its comments?')) return; wrap(p.id, () => deletePost(p.id), true); }}
                    className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">
                    Delete
                  </button>
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
            <span>Showing {rows.length} of {total}</span>
            {page < pageCount && (
              <button onClick={() => load(page + 1, q, true)}
                className="px-3 py-1.5 rounded-md bg-white border border-gray-200 hover:bg-gray-50 font-semibold text-gray-700">
                Load more
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Comments panel ---------- */
function CommentsPanel() {
  const [filter, setFilter] = useState<'reported' | 'hidden' | 'all'>('reported');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try { const res = await fetchCommunityComments(filter); setRows(res.data || []); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const wrap = async (id: number, fn: () => Promise<any>) => {
    setBusy(id);
    try { await fn(); await load(); }
    catch { alert('Action failed'); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex gap-1 inline-flex mb-3">
        {(['reported','hidden','all'] as const).map((f) => (
          <button key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            {f === 'reported' ? 'Reported' : f === 'hidden' ? 'Hidden' : 'All'}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-500">No comments match.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5">Comment</th>
                <th className="px-4 py-2.5">Post</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs font-semibold">{c.user?.username || '—'}</td>
                  <td className="px-4 py-3 text-xs max-w-md truncate">{c.content}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">#{c.post?.id || '—'}</td>
                  <td className="px-4 py-3">
                    {c.is_hidden ? <span className="text-[11px] font-semibold text-red-700">Hidden</span> : <span className="text-[11px] text-green-700">Visible</span>}
                    {c.report_count > 0 && <span className="ml-2 text-[10px] font-bold text-red-700">🚩 {c.report_count}</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {c.report_count > 0 && (
                        <button disabled={busy === c.id} onClick={() => wrap(c.id, () => clearCommentReports(c.id))}
                          className="text-[11px] px-2 py-1 rounded-md bg-gray-50 hover:bg-gray-100 font-semibold disabled:opacity-50">Clear</button>
                      )}
                      <button disabled={busy === c.id} onClick={() => wrap(c.id, () => c.is_hidden ? unhideComment(c.id) : hideComment(c.id))}
                        className={`text-[11px] px-2 py-1 rounded-md font-semibold disabled:opacity-50 ${c.is_hidden ? 'bg-primary text-white hover:opacity-90' : 'bg-yellow-50 text-yellow-800 hover:bg-yellow-100'}`}>
                        {c.is_hidden ? 'Restore' : 'Hide'}
                      </button>
                      <button disabled={busy === c.id} onClick={() => { if (!window.confirm('Delete this comment?')) return; wrap(c.id, () => deleteComment(c.id)); }}
                        className="text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Categories panel ---------- */

function CategoriesPanel() {
  const [rows, setRows] = useState<CommunityCategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('');

  const load = async () => {
    setLoading(true);
    try { const res = await fetchCommunityCategories(); setRows(res.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await createCommunityCategory({ name: newName.trim(), icon: newIcon.trim() || null, is_active: true, sortOrder: rows.length });
      setNewName(''); setNewIcon('');
      await load();
    } catch (e: any) { alert(e?.response?.data?.error || 'Failed to create'); }
    finally { setBusy(false); }
  };

  const patch = async (id: number, data: Partial<CommunityCategoryRow>) => {
    try { await updateCommunityCategory(id, data); await load(); }
    catch { alert('Update failed'); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this category? Posts under it will become uncategorized.')) return;
    try { await deleteCommunityCategory(id); await load(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-800 mb-3">Add category</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (e.g. Recipes)"
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          <input value={newIcon} onChange={(e) => setNewIcon(e.target.value)} placeholder="Icon (emoji or text)"
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          <button disabled={busy || !newName.trim()} onClick={create}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
            Add
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-12 bg-white rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2.5">Icon</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Slug</th>
                <th className="px-4 py-2.5">Order</th>
                <th className="px-4 py-2.5">Active</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-lg">{c.icon || '—'}</td>
                  <td className="px-4 py-2 font-semibold text-gray-800">{c.name}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{c.slug}</td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      defaultValue={c.sortOrder}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v) && v !== c.sortOrder) patch(c.id, { sortOrder: v });
                      }}
                      className="w-16 px-2 py-1 text-xs rounded border border-gray-200"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => patch(c.id, { is_active: !c.is_active })}
                      className={`text-[11px] px-2 py-1 rounded-md font-semibold ${c.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => remove(c.id)} className="text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No categories yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
